import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnDestroy,
    ViewChild,
    computed,
    inject,
    input,
    output,
    signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    ControlContainer,
    FormControl,
    FormsModule,
    ReactiveFormsModule
} from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import type { Subject } from 'rxjs';
import { AncFieldValidateDirective } from '../../form-validation/anc-field-validate.directive';
import { AncKeepInViewDirective } from '../../directives/anc-keep-in-view.directive';
import { ancShowFieldError } from '../../form-validation/anc-show-field-error.util';

/** Matches ng-select `AddTagFn` — may return an item object or a Promise. */
export type AncAddTagFn = (term: string) => unknown | Promise<unknown>;

/** Per-instance ids for labelForId; module-scoped so they stay unique. */
let ancSelectUid = 0;

/**
 * Dropdown field shell with Figma outlined floating label.
 *
 * **Option A — projection (default):** developer supplies `<ng-select>` + optional
 * `[ancError]`. Use for custom templates, virtualScroll, appendTo, etc.
 *
 * @example Option A
 * <lib-anc-select [label]="'Warehouse'" [required]="true">
 *   <ng-select formControlName="warehouse_id" [items]="list" bindLabel="name"
 *     bindValue="id" ancFieldValidate [ancFieldValidateSubmitted]="submitted"></ng-select>
 * </lib-anc-select>
 *
 * **Option B — declarative:** set `controlName`; lib renders `<ng-select>` internally.
 * Supports typeahead, virtualScroll, scrollToEnd, translateOptions, and built-in validation.
 *
 * @example Option B
 * <lib-anc-select [label]="'Storage Type'" controlName="storage_type"
 *   [items]="storageTypesList" bindLabel="name" bindValue="storage_type_id"
 *   [requiredErrorMessage]="'Storage Type is required'"
 *   [ancFieldValidateSubmitted]="submitted"
 *   (selectionChange)="handleStorageTypeChange($event)"></lib-anc-select>
 */
@Component({
    selector: 'lib-anc-select',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        NgSelectModule,
        TranslateModule,
        AncFieldValidateDirective
    ],
    templateUrl: './anc-select.component.html',
    styleUrls: ['./anc-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    hostDirectives: [AncKeepInViewDirective],
    host: {
        '(focusin)': 'onFocusIn()',
        '(focusout)': 'onFocusOut()',
        '(change)': 'updateHasValue()'
    }
})
export class AncSelectComponent implements AfterViewInit, OnDestroy {
    /**
     * ng-select renders role="combobox" on its inner <input>, not on the host, so
     * the visible .anc-field__label has nothing to point at. labelForId puts this id
     * on that input and the label's [for] associates with it — a real label rather
     * than only an aria-label override, which also makes the label clickable.
     */
    readonly inputId = `anc-select-${++ancSelectUid}`;

    /** Field label text (run through the translate pipe). */
    label = input<string>('');
    /** Renders the required asterisk when true. */
    required = input<boolean>(false);

    /** Reactive-forms control name — when set, lib renders ng-select internally. */
    controlName = input<string>('');
    items = input<readonly unknown[]>([]);
    bindLabel = input<string>('');
    bindValue = input<string>('');
    clearable = input<boolean>(true);
    /** Option B — ng-select type-to-filter; set false for short fixed lists (e.g. warehouse). */
    searchable = input<boolean>(true);
    readonly = input<boolean>(false);
    loading = input<boolean>(false);
    /** Option B — multi-select mode. */
    multiple = input<boolean>(false);
    /** Option B — keep dropdown open after each selection (multi-select). */
    closeOnSelect = input<boolean>(true);
    /** Option B — checkbox row template for multi-select dropdown options. */
    checkboxOptions = input<boolean>(false);
    /**
     * Option B — max individual name badges shown before appending a "+N" overflow badge
     * (Figma node 259-620).
     */
    multiBadgeLimit = input<number>(2);
    /** Option B — ng-select empty-state message (run through translate pipe). */
    notFoundText = input<string>('No items found');
    /** Option B — blur active element after change (replaces legacy `appRemoveFocus`). */
    removeFocusOnChange = input<boolean>(false);
    /** Option B — ng-select virtual scroll for long lists. */
    virtualScroll = input<boolean>(false);
    /** Option B — async search stream for ng-select typeahead. */
    typeahead = input<Subject<string> | null>(null);
    /** Option B — translate option/label text (string items or bindLabel values). */
    translateOptions = input<boolean>(false);
    /** Option B — portal target for the dropdown panel (e.g. `'body'`). */
    appendTo = input<string>('');
    /** Option B — ng-select panel placement. */
    dropdownPosition = input<'auto' | 'top' | 'bottom'>('auto');
    /** Option B — allow creating new options from the search term (`true` or validator fn). */
    addTag = input<boolean | AncAddTagFn>(false);
    /** Option B — label for the add-tag row in the dropdown (ng-select `addTagText`). */
    addTagText = input<string>('');
    /** Forwards to embedded ng-select `ancFieldValidate` (invalid border when submitted). */
    validateSubmitted = input(false, { alias: 'ancFieldValidateSubmitted' });
    /** Option B — required validation message (rendered internally; no projected `[ancError]`). */
    requiredErrorMessage = input<string>('');

    /** Mirrors ng-select `(change)` for Option B. */
    selectionChange = output<unknown>();
    /** Mirrors ng-select `(search)` for Option B typeahead setup. */
    searchChange = output<{ term: string; items: unknown[] }>();
    /** Mirrors ng-select `(scrollToEnd)` for Option B infinite scroll. */
    scrollToEnd = output<void>();
    /** Mirrors ng-select `(keydown)` for Option B. */
    keydown = output<KeyboardEvent>();

    private readonly host = inject(ElementRef<HTMLElement>);
    /** Parent `[formGroup]` / `formGroupName` — not visible to `formControlName` inside this template. */
    private readonly parentForm = inject(ControlContainer, {
        optional: true,
        skipSelf: true
    });

    @ViewChild(NgSelectComponent)
    private readonly embeddedSelect?: NgSelectComponent;

    protected readonly focused = signal(false);
    protected readonly hasValue = signal(false);
    protected readonly floating = computed(
        () => this.focused() || this.hasValue()
    );

    private observer?: MutationObserver;
    private readonly closeOnOutsideScroll = (event: Event): void => {
        if (!this.embeddedSelect?.isOpen) {
            return;
        }
        const target = event.target;
        // `appendTo` moves the dropdown panel to <body>, so it never follows an
        // ancestor's scroll — close it instead of leaving it stranded mid-page.
        // Scrolling inside the panel's own option list must not trigger this.
        if (target instanceof Element && target.closest('.ng-dropdown-panel')) {
            return;
        }
        this.embeddedSelect.close();
    };

    ngAfterViewInit(): void {
        this.updateHasValue();
        Promise.resolve().then(() => this.updateHasValue());
        this.observer = new MutationObserver(() => this.updateHasValue());
        this.observer.observe(this.host.nativeElement, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['class']
        });
        if (this.appendTo()) {
            document.addEventListener(
                'scroll',
                this.closeOnOutsideScroll,
                true
            );
        }
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();
        document.removeEventListener('scroll', this.closeOnOutsideScroll, true);
    }

    protected onFocusIn(): void {
        this.focused.set(true);
    }

    protected onFocusOut(): void {
        this.focused.set(false);
        this.updateHasValue();
    }

    protected onEmbeddedChange(value: unknown): void {
        this.selectionChange.emit(value);
        this.updateHasValue();
        if (this.removeFocusOnChange()) {
            (document.activeElement as HTMLElement | null)?.blur();
        }
    }

    /**
     * Resolves the reactive control from the parent form for embedded mode.
     * `formControlName` cannot be used on ng-select inside this component template.
     */
    protected embeddedControl(): FormControl | null {
        const name = this.controlName();
        if (!name) {
            return null;
        }
        const group = this.parentForm?.control;
        if (!name || !group) {
            return null;
        }
        const control = group.get(name);
        return control instanceof FormControl ? control : null;
    }

    /** Whether embedded mode should show the built-in required error slot. */
    protected showEmbeddedRequiredError(control: FormControl): boolean {
        return (
            !!this.requiredErrorMessage() &&
            !!control.errors?.['required'] &&
            ancShowFieldError(control, this.validateSubmitted())
        );
    }

    protected updateHasValue(): void {
        const root = this.host.nativeElement as HTMLElement;
        const filled = !!root.querySelector('.ng-has-value, .ng-value');
        this.hasValue.set(filled);
    }

    /** Resolves nested bindLabel paths (e.g. `attributes.value`) for custom option templates. */
    protected resolveOptionLabel(item: unknown): string {
        const path = this.bindLabel();
        if (!path) {
            return item != null ? String(item) : '';
        }
        const value = path.split('.').reduce<unknown>((current, key) => {
            if (current != null && typeof current === 'object') {
                return (current as Record<string, unknown>)[key];
            }
            return undefined;
        }, item);
        if (value == null || value === '') {
            return item != null ? String(item) : '';
        }
        return String(value);
    }

    /** Resolves the display label for a selected value (primitive or object). */
    protected resolveSelectedValueLabel(selectedValue: unknown): string {
        const bindValueKey = this.bindValue();
        const sourceItems = this.items();

        if (bindValueKey && sourceItems.length > 0) {
            const matched = sourceItems.find(
                (item) =>
                    this.readPathValue(item, bindValueKey) === selectedValue
            );
            if (matched != null) {
                return this.resolveOptionLabel(matched);
            }
        }

        return this.resolveOptionLabel(selectedValue);
    }

    /** Whether the multi-select should collapse overflow into a "+N" badge. */
    protected shouldCollapseMultiLabels(items: readonly unknown[]): boolean {
        return items.length > this.multiBadgeLimit();
    }

    /** First N selected items shown as individual badges when overflow is collapsed. */
    protected visibleMultiLabels(
        items: readonly unknown[]
    ): readonly unknown[] {
        return items.slice(0, this.multiBadgeLimit());
    }

    /** Count of selections hidden behind the "+N" overflow badge. */
    protected remainingMultiLabelCount(items: readonly unknown[]): number {
        return Math.max(0, items.length - this.multiBadgeLimit());
    }

    private readPathValue(source: unknown, path: string): unknown {
        return path.split('.').reduce<unknown>((current, key) => {
            if (current != null && typeof current === 'object') {
                return (current as Record<string, unknown>)[key];
            }
            return undefined;
        }, source);
    }
}
